import {
  InmarDisposition,
  InmarDispositionAction,
  InmarItemIdResolutionSource,
} from "@redotech/redo-return-core-common/integrations/returns/inmar/inmar";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Secrets are write-only: the write schema accepts them (blank preserves the
// stored value), the read schema only reports whether one is stored.
const inmarEnvironmentConfigWriteSchema = z.object({
  consumerId: z.string().nullish(),
  accountIds: z.array(z.string()).nullish(),
  clientId: z.string().nullish(),
  clientSecret: z.string().nullish(),
  webhookSecret: z.string().nullish(),
});

const inmarEnvironmentConfigReadSchema = z.object({
  consumerId: z.string().nullish(),
  accountIds: z.array(z.string()).nullish(),
  clientId: z.string().nullish(),
  hasClientSecret: z.boolean(),
  hasWebhookSecret: z.boolean(),
});

const inmarItemIdResolutionSchema = z.object({
  universalBarcode: createEnum(InmarItemIdResolutionSource).default(
    InmarItemIdResolutionSource.UPC_BARCODE,
  ),
  itemKey: createEnum(InmarItemIdResolutionSource).default(
    InmarItemIdResolutionSource.SKU,
  ),
});

const inmarCapturedResponseConfigSchema = z.object({
  name: z.string(),
  value: z.string(),
  action: createEnum(InmarDispositionAction),
  restock: z.boolean(),
});

const inmarDispositionMappingSchema = z.object({
  disposition: createEnum(InmarDisposition),
  capturedResponses: z.array(inmarCapturedResponseConfigSchema),
  defaultAction: createEnum(InmarDispositionAction),
  defaultRestock: z.boolean(),
});

export const inmarIntegrationWriteSchema = z.object({
  enabled: z.boolean(),
  developmentConfig: inmarEnvironmentConfigWriteSchema,
  productionConfig: inmarEnvironmentConfigWriteSchema,
  isRolledUpUnderRedo: z.boolean().optional().default(true),
  itemIdResolution: inmarItemIdResolutionSchema,
  dispositionMapping: z.array(inmarDispositionMappingSchema),
});

export const inmarIntegrationReadSchema = z.object({
  enabled: z.boolean(),
  developmentConfig: inmarEnvironmentConfigReadSchema,
  productionConfig: inmarEnvironmentConfigReadSchema,
  isRolledUpUnderRedo: z.boolean().optional().default(true),
  itemIdResolution: inmarItemIdResolutionSchema,
  dispositionMapping: z.array(inmarDispositionMappingSchema),
});

export type InmarIntegration = z.infer<typeof inmarIntegrationReadSchema>;

export const getInmarIntegration = {
  name: "getInmarIntegration" as const,
  input: z.object({}),
  output: z.object({ inmarIntegration: inmarIntegrationReadSchema.nullable() }),
};

export const setInmarIntegration = {
  name: "setInmarIntegration" as const,
  // Secret requirements are enforced server-side at create time only: on update,
  // a blank secret preserves the stored value, so it can't be required here.
  input: inmarIntegrationWriteSchema,
  output: z.object({}),
};

export const disconnectInmarIntegration = {
  name: "disconnectInmarIntegration" as const,
  input: z.object({}),
  output: z.object({}),
};
